import React, { useEffect, useState } from "react";
import { Card, Button, Form, Row, Col } from "react-bootstrap";
import axios from "../../../interceptor/axios-config";
import Swal from "sweetalert2";
import config from "../../../config";
import { currentSession } from "../../../utils/session";
import { showToast } from "../../../utils/toast";

const months = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December"
];

const GenerateFeesDashboard = () => {

  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState("");
  const [month, setMonth] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [selectedSections, setSelectedSections] = useState([]);


  // toggle section 
  const toggleSection = (sec) => {
    setSelectedSections(prev =>
      prev.includes(sec)
        ? prev.filter(s => s !== sec)
        : [...prev, sec]
    );
  };
  // load classes
  useEffect(() => {
    axios
      .get(`${config.apiBaseUrl}/grades/classes.php?action=list`)
      .then(res => setClasses(res.data || []));
  }, []);

  const toggleMonth = (m) => {
      setSelectedMonths(prev =>
        prev.includes(m)
          ? prev.filter(x => x !== m)
          : [...prev, m]
      );
  };

  const checkAllMonths = () => {
    setSelectedMonths(months.filter(m => m !== "January"));
  };

  const clearMonths = () => {
    setSelectedMonths([]);
  };


  const handleGenerate = async () => {
      if (!classId) {
        showToast("warning", "Please select a class.");
        return;
      }

      if (selectedSections.length === 0) {
        showToast("warning", "Please select at least one section.");
        return;
      }

      if (selectedMonths.length === 0) {
        showToast("warning", "Please select at least one month.");
        return;
      }

      // 🔥 Find matching class records
      const selectedClassObjects = classes.filter(
        c =>
          c.grade_id == classId &&
          selectedSections.includes(c.section_name)
      );

      if (selectedClassObjects.length === 0) {
        showToast("error", "No matching classes found.");
        return;
      }

      const classIds = selectedClassObjects.map(c => c.class_id);

      try {
        setLoading(true);

        await axios.post(
          `${config.apiBaseUrl}/fees/generate-monthly-fees.php`,
          {
            class_ids: classIds,       // 🔥 multiple class_ids
            months: selectedMonths,
            academic_year: currentSession
          }
        );

        showToast("success", "Fees generated successfully!");
        setSelectedMonths([]);
        setSelectedSections([]);

      } catch (err) {
        console.error(err);
        showToast("error", "Failed to generate fees.");
      } finally {
        setLoading(false);
      }
    };

    const availableSections = classId
      ? [...new Set(
          classes
            .filter(c => c.grade_id == classId)
            .map(c => c.section_name)
        )]
      : [];

  return (
    <Card className="p-4">
      <h4 className="mb-3">Generate Monthly Fees</h4>

      <Row className="mb-3">

        {/* Grade Dropdown */}
        <Col md={6}>
          <Form.Label>Class</Form.Label>
          <Form.Select
            value={classId}
            onChange={e => {
              const selectedGrade = e.target.value;
              setClassId(selectedGrade);

              if (selectedGrade) {
                const sectionsForGrade = [
                  ...new Set(
                    classes
                      .filter(c => c.grade_id == selectedGrade)
                      .map(c => c.section_name)
                  )
                ];

                setSelectedSections(sectionsForGrade); // 🔥 auto-check all sections
              } else {
                setSelectedSections([]);
              }
            }}
          >
            <option value="">Select Class</option>

            {[...new Map(classes.map(c =>
              [c.grade_id, c]
            )).values()].map(c => (
              <option key={c.grade_id} value={c.grade_id}>
                {c.grade_name} ({c.academic_year})
                {/* Class-{c.grade_name} ({c.academic_year}) */}
              </option>
            ))}
          </Form.Select>
        </Col>

        {/* Section Checkboxes */}
        <Col md={6}>
          <Form.Label>Sections</Form.Label>

          {!classId ? (
            <div className="text-muted">Select class first</div>
          ) : (
            <Row>
              {availableSections.map(sec => (
                <Col md={4} key={sec}>
                  <Form.Check
                    type="checkbox"
                    label={sec}
                    checked={selectedSections.includes(sec)}
                    onChange={() => toggleSection(sec)}
                  />
                </Col>
              ))}
            </Row>
          )}
        </Col>

        {/* Months */}
        <Col md={12} className="mt-3">
          <Form.Label>Months</Form.Label>

          <div className="mb-2">
            <Button
              size="sm"
              variant="outline-primary"
              className="me-2"
              onClick={checkAllMonths}
            >
              Check All
            </Button>

            <Button
              size="sm"
              variant="outline-secondary"
              onClick={clearMonths}
            >
              Clear
            </Button>
          </div>

          <Row>
            {months.map(m => (
              <Col md={3} key={m}>
                <Form.Check
                  type="checkbox"
                  label={m}
                  checked={selectedMonths.includes(m)}
                  disabled={m === "January"}
                  onChange={() => toggleMonth(m)}
                />
              </Col>
            ))}
          </Row>
        </Col>

      </Row>

      <div className="mb-3">
        <strong>Session:</strong> {currentSession}
      </div>

      <Button
        variant="primary"
        onClick={handleGenerate}
        disabled={
          loading ||
          !classId ||
          selectedSections.length === 0 ||
          selectedMonths.length === 0
        }
      >
        {loading ? "Generating..." : "Generate Fees"}
      </Button>
    </Card>

  );
};

export default GenerateFeesDashboard;
